import { TabPanel } from 'react-aria-components';
import { Loading } from '@hungryroot/loading-component-primitives';
import { type PeriodicityTypes } from '@hungryroot/rest';
import { Heading } from '@hungryroot/ui-kit';
import { useSelectedGroceryCategory } from '../hooks/useSelectedGroceryCategory';
import { GroceryCategoryCard } from './GroceryCategoryCard';
import { generateTabId } from './GroceryCategoryTabList';
import { GroceryItemCard } from './GroceryItemCard';
import styles from './GroceryCategoryTabPanel.module.css';

type GroceryCategoryTabPanelProps = {
  selectedCategory: number;

  groceries: {
    id: number;
    isNew: boolean;
    cardImageUrl: string | null;
    brandName: string;
    periodicity: PeriodicityTypes | null;
    name: string;
  }[];

  isGroceriesLoading: boolean;
};

export function GroceryCategoryTabPanel(props: GroceryCategoryTabPanelProps) {
  const { selectedCategory, groceries, isGroceriesLoading } = props;

  const { selectedCategory: selectedCategoryData } = useSelectedGroceryCategory(
    { selectedCategory }
  );

  return (
    <TabPanel
      className={styles['tab-panel']}
      id={generateTabId(selectedCategory)}
    >
      {selectedCategoryData?.name && groceries.length > 0 ? (
        <Heading className={styles.heading} variant="H3">
          {selectedCategoryData.name}
        </Heading>
      ) : null}
      <div className={styles['tab-panel-content']}>
        {isGroceriesLoading ? (
          <div className={styles['loading-container']}>
            <Loading variant="inline-button" />
          </div>
        ) : (
          <GroceryCards
            selectedCategory={selectedCategory}
            groceries={groceries}
          />
        )}
      </div>
    </TabPanel>
  );
}

function GroceryCards(
  props: Pick<GroceryCategoryTabPanelProps, 'selectedCategory' | 'groceries'>
) {
  const { selectedCategory, groceries } = props;

  if (groceries.length === 0) {
    return <NoGroceries />;
  }

  return (
    <>
      <GroceryCategoryCard selectedCategory={selectedCategory} />
      {groceries.map((grocery) => (
        <GroceryItemCard key={grocery.id} grocery={grocery} />
      ))}
    </>
  );
}

function NoGroceries() {
  return (
    <div className={styles['no-groceries']}>
      <Heading className={styles['no-groceries-heading']} variant="H2">
        Whoops! Looks like this category is empty at the moment.
      </Heading>
      <img
        src="/public/img/edit_delivery/empty-illustration.svg"
        alt="Empty box"
      />
    </div>
  );
}
